/**
 * HomeTab.js — Landing dashboard for the Zendesk AI Testing Sandbox
 *
 * Shows on first visit only (localStorage flag), then remembers last tab.
 * Contains:
 *   - Welcome strip (first-time only, dismissible)
 *   - Setup checklist with live status
 *   - Live stats row (scenarios run, pass rate, active config, Zendesk)
 *   - Tab overview cards (clickable, navigate directly)
 */

import React, { useState, useEffect } from 'react';
import { useApi } from './hooks/useApi';

// ── Tab definitions ───────────────────────────────────────────────────────────

const TAB_CARDS = [
  {
    id: 'scenarios',
    icon: '📋',
    name: 'Scenarios',
    tagline: 'Test your AI agent against 38 pre-built situations',
    description:
      'Run your agent against common support scenarios, edge cases, and 21 adversarial security tests — including prompt injection, PII extraction attempts, and policy manipulation. Each test returns a scored conversation with pass/fail determination.',
    highlight: 'OWASP ranks prompt injection as the #1 AI vulnerability — present in 73% of deployments.',
    highlightColor: '#a78bfa',
    cta: 'Run Scenarios →',
    gated: false,
  },
  {
    id: 'zendesk',
    icon: '🎫',
    name: 'Zendesk Tickets',
    tagline: 'Import real tickets and test against your actual data',
    description:
      'Connect your Zendesk account and import historical support tickets. Your agent is tested against real customer conversations — not synthetic data. All PII is automatically anonymized before any data enters the platform.',
    highlight: null,
    cta: 'Import Tickets →',
    gated: false,
  },
  {
    id: 'agent-config',
    icon: '🤖',
    name: 'Agent Config',
    tagline: 'Configure the AI agent being tested',
    description:
      'Define your AI agent\'s system prompt, knowledge base, and escalation rules. ' +
      'Level 1 uses GPT-4o to simulate your agent — no integration needed, ready immediately. ' +
      'Level 2 points directly at your live deployed agent endpoint (OpenAI-compatible, Anthropic, Zendesk Sunshine, or custom) so you\'re testing the real thing. ' +
      'The active config is used across all test tabs.',
    highlight: 'Switch between simulation and live endpoint without changing your test scenarios.',
    highlightColor: '#38bdf8',
    cta: 'Configure Agent →',
    gated: false,
  },
  {
    id: 'benchmark',
    icon: '⚖️',
    name: 'Benchmark',
    tagline: 'Compare AI performance against your human agents',
    description:
      'The only pre-deployment tool that answers "Is AI actually better than my human agents?" Run the same tickets through both and get a side-by-side score comparison with ROI projection.',
    highlight: 'No competitor offers Human vs AI benchmarking with your own ticket data.',
    highlightColor: '#10b981',
    cta: 'Run Benchmark →',
    gated: true,
  },
];

// ── Helper components ─────────────────────────────────────────────────────────

function StatCard({ icon, value, label, sublabel, color = '#7c3aed' }) {
  return (
    <div style={{
      background: '#1a1a2e', border: '1px solid #2d2d4e', borderRadius: 10,
      padding: '16px 20px', flex: 1, minWidth: 140,
    }}>
      <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 600, marginTop: 4 }}>{label}</div>
      {sublabel && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{sublabel}</div>}
    </div>
  );
}

function ChecklistItem({ done, label, sublabel, onClick }) {
  return (
    <div
      onClick={done ? undefined : onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 14px', borderRadius: 8,
        background: done ? 'rgba(16,185,129,0.04)' : 'rgba(124,58,237,0.04)',
        border: `1px solid ${done ? 'rgba(16,185,129,0.2)' : '#2d2d4e'}`,
        cursor: done ? 'default' : 'pointer',
        transition: 'all 0.15s',
      }}
      onMouseEnter={e => { if (!done) e.currentTarget.style.borderColor = '#7c3aed'; }}
      onMouseLeave={e => { if (!done) e.currentTarget.style.borderColor = '#2d2d4e'; }}
    >
      <div style={{
        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: done ? '#10b981' : '#1f1f3a',
        border: `2px solid ${done ? '#10b981' : '#374151'}`,
        fontSize: 12, color: '#fff',
      }}>
        {done ? '✓' : ''}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: done ? '#94a3b8' : '#e2e8f0' }}>
          {label}
        </div>
        {sublabel && (
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>{sublabel}</div>
        )}
      </div>
      {!done && (
        <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>Set up →</div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function HomeTab({
  onNavigate,
  activeAgentConfig,
  hasKbArticles,
  billing,
  isDemo = false,
  betaCta = '',
  onDemoReset = null,
}) {
  const { authFetch } = useApi();

  const [resetting, setResetting] = useState(false);

  const handleDemoReset = async () => {
    if (!window.confirm('Reset the demo workspace to its original state? This will clear all run results, imported tickets, and agent configs, then restore the pre-populated demo data.')) return;
    setResetting(true);
    try {
      await authFetch('/api/demo/reset', { method: 'POST' });
      // Clear the demo callout dismissed flag so it shows again on reload
      localStorage.removeItem('demo_callout_dismissed');
      // Notify App.js to refresh shared state (active config, KB status, etc.)
      if (onDemoReset) onDemoReset();
      // Hard reload so all tabs pick up fresh data
      window.location.reload();
    } catch (err) {
      alert('Reset failed — please try again.');
      setResetting(false);
    }
  };

  // Demo callout — show once per session, dismissible
  const [showDemoCallout, setShowDemoCallout] = useState(
    () => isDemo && localStorage.getItem('demo_callout_dismissed') !== 'true'
  );

  const dismissDemoCallout = () => {
    setShowDemoCallout(false);
    localStorage.setItem('demo_callout_dismissed', 'true');
  };

  // Welcome strip — show once
  const [showWelcome, setShowWelcome] = useState(
    () => localStorage.getItem('welcome_dismissed') !== 'true'
  );

  const dismissWelcome = () => {
    setShowWelcome(false);
    localStorage.setItem('welcome_dismissed', 'true');
  };

  // Live data
  const [zendeskStatus, setZendeskStatus] = useState(null);
  const [scenarioStats, setScenarioStats] = useState(null);
  // Derived from prop so it stays in sync when App.js loads the active config after mount
  const [hasAgentConfigFallback, setHasAgentConfigFallback] = useState(false);
  const hasAgentConfig = !!activeAgentConfig || hasAgentConfigFallback;
  const [benchmarkStats, setBenchmarkStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [zdStatus, stats, benchSummary] = await Promise.allSettled([
          authFetch('/api/zendesk/oauth/status'),
          authFetch('/api/scenarios/stats'),
          authFetch('/api/benchmark/summary'),
        ]);

        if (zdStatus.status === 'fulfilled') setZendeskStatus(zdStatus.value);
        if (stats.status === 'fulfilled') setScenarioStats(stats.value);
        if (benchSummary.status === 'fulfilled') setBenchmarkStats(benchSummary.value);

        // Agent config fallback — only fetch if prop not yet populated
        if (!activeAgentConfig) {
          try {
            const res = await authFetch('/api/agent-configs');
            const configs = res?.configs || (Array.isArray(res) ? res : []);
            setHasAgentConfigFallback(configs.length > 0);
          } catch {}
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authFetch, activeAgentConfig]);

  // ── Checklist state ──────────────────────────────────────────────────────

  const checklistItems = [
    {
      done: !!zendeskStatus?.connected,
      label: 'Connect your Zendesk account',
      sublabel: zendeskStatus?.connected
        ? `Connected to ${zendeskStatus.subdomain}.zendesk.com`
        : 'Import real tickets for more realistic testing',
      nav: 'zendesk',
    },
    {
      done: hasAgentConfig,
      label: 'Create an Agent Config',
      sublabel: activeAgentConfig
        ? `Active: ${activeAgentConfig.name}`
        : 'Define your agent\'s prompt and KB — or connect a live endpoint for real agent testing',
      nav: 'agent-config',
    },
    {
      done: !!hasKbArticles,
      label: 'Load Knowledge Base articles',
      sublabel: hasKbArticles
        ? 'KB articles available — select them in Agent Config → Global KB'
        : 'Ground your agent in your actual Help Center content',
      nav: 'agent-config',
    },
    {
      done: !!(scenarioStats?.has_runs),
      label: 'Run your first scenario',
      sublabel: scenarioStats?.has_runs
        ? `${scenarioStats.total_runs} run${scenarioStats.total_runs !== 1 ? 's' : ''} completed`
        : 'Test your agent and see a scored conversation result',
      nav: 'scenarios',
    },
  ];

  const checklistDone = checklistItems.filter(i => i.done).length;
  const allDone = checklistDone === checklistItems.length;

  // ── Stats row ────────────────────────────────────────────────────────────

  const passRate = scenarioStats?.pass_rate ?? null;
  const passRateColor = passRate === null ? '#6b7280' : passRate >= 70 ? '#10b981' : passRate >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <div style={{ padding: '28px 32px', maxWidth: 860 }}>

      {/* Demo environment callout — dismissible, shown only on demo workspaces */}
      {showDemoCallout && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(99,102,241,0.06) 100%)',
          border: '1px solid rgba(16,185,129,0.3)', borderRadius: 12,
          padding: '20px 24px', marginBottom: 28,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 18 }}>🔬</span>
              <span style={{ fontSize: 15, fontWeight: 800, color: '#10b981' }}>
                You're exploring a demo workspace
              </span>
              <span style={{
                fontSize: 10, fontWeight: 700, color: '#10b981',
                background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)',
                borderRadius: 4, padding: '1px 7px', letterSpacing: '0.06em',
              }}>
                SAMPLE DATA
              </span>
            </div>
            <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6, maxWidth: 600, marginBottom: 14 }}>
              This workspace is pre-populated with data for a fictional SaaS company so you can
              explore every feature — 40 support tickets, two agent configs, four simulation runs,
              a Human vs AI Benchmark, and an A/B comparison. All setup steps are already complete.
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <a
                href={betaCta}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#10b981', color: '#fff', textDecoration: 'none',
                  borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 700,
                }}
              >
                Apply for Beta Access →
              </a>
              <button
                onClick={dismissDemoCallout}
                style={{
                  background: 'transparent', border: '1px solid #374151',
                  color: '#6b7280', borderRadius: 8, padding: '8px 16px',
                  fontSize: 13, cursor: 'pointer',
                }}
              >
                Explore the demo
              </button>
              <button
                onClick={handleDemoReset}
                disabled={resetting}
                title="Wipe run results and restore original demo data"
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(239,68,68,0.35)',
                  color: resetting ? '#6b7280' : '#f87171',
                  borderRadius: 8, padding: '8px 16px',
                  fontSize: 13, cursor: resetting ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                {resetting ? '⟳ Resetting…' : '↺ Reset Demo'}
              </button>
            </div>
          </div>
          <button
            onClick={dismissDemoCallout}
            style={{
              background: 'none', border: 'none', color: '#6b7280',
              cursor: 'pointer', fontSize: 18, padding: '0 4px', flexShrink: 0,
            }}
          >×</button>
        </div>
      )}

      {/* Welcome strip — first visit only */}
      {showWelcome && !isDemo && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(99,102,241,0.08) 100%)',
          border: '1px solid rgba(124,58,237,0.3)', borderRadius: 12,
          padding: '20px 24px', marginBottom: 28,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16,
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#e2e8f0', marginBottom: 6 }}>
              👋 Welcome to your AI Testing Sandbox
            </div>
            <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6, maxWidth: 560 }}>
              This platform lets you test your Zendesk AI agent against real customer scenarios
              — including adversarial security tests — before going live. Complete the setup steps
              below and run your first scenario to see a scored conversation result.
            </div>
          </div>
          <button
            onClick={dismissWelcome}
            style={{
              background: 'none', border: 'none', color: '#6b7280',
              cursor: 'pointer', fontSize: 18, padding: '0 4px', flexShrink: 0,
            }}
          >×</button>
        </div>
      )}

      {/* Setup checklist */}
      {!allDone && (
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <h3 style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 15, margin: 0 }}>
                Getting Started
              </h3>
              <p style={{ color: '#6b7280', fontSize: 12, margin: '3px 0 0' }}>
                {checklistDone} of {checklistItems.length} steps complete
              </p>
            </div>
            {/* Progress bar */}
            <div style={{ width: 120, height: 6, background: '#1f1f3a', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 3,
                width: `${(checklistDone / checklistItems.length) * 100}%`,
                background: 'linear-gradient(90deg, #7c3aed, #6366f1)',
                transition: 'width 0.4s ease',
              }} />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {checklistItems.map((item, i) => (
              <ChecklistItem
                key={i}
                done={item.done}
                label={item.label}
                sublabel={item.sublabel}
                onClick={() => onNavigate(item.nav)}
              />
            ))}
          </div>
        </div>
      )}

      {/* All done celebration */}
      {allDone && (
        <div style={{
          background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)',
          borderRadius: 10, padding: '14px 20px', marginBottom: 28,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 20 }}>🎉</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#10b981' }}>Setup complete</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Your sandbox is fully configured. Keep running scenarios to build your performance baseline.
            </div>
          </div>
        </div>
      )}

      {/* Live stats row */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 15, margin: '0 0 14px' }}>
          Workspace Overview
        </h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <StatCard
            icon="🧪"
            value={loading ? '—' : (scenarioStats?.total_runs ?? 0)}
            label="Scenarios Run"
            sublabel={scenarioStats?.total ? `${scenarioStats.total} available` : null}
            color="#7c3aed"
          />
          <StatCard
            icon="✅"
            value={loading ? '—' : (passRate !== null ? `${passRate}%` : '—')}
            label="Pass Rate"
            sublabel={scenarioStats?.has_runs ? `${scenarioStats.passed} passed / ${scenarioStats.failed} failed` : 'No runs yet'}
            color={passRateColor}
          />
          <StatCard
            icon="🤖"
            value={activeAgentConfig ? '✓' : '—'}
            label="Active Config"
            sublabel={activeAgentConfig?.name || 'None set'}
            color={activeAgentConfig ? '#10b981' : '#6b7280'}
          />
          <StatCard
            icon="🎫"
            value={loading ? '—' : (zendeskStatus?.connected ? '✓' : '—')}
            label="Zendesk"
            sublabel={zendeskStatus?.connected ? zendeskStatus.subdomain : 'Not connected'}
            color={zendeskStatus?.connected ? '#10b981' : '#6b7280'}
          />
          {billing?.features?.benchmark && (
            <StatCard
              icon="⚖️"
              value={benchmarkStats?.summary?.ai_win_rate != null
                ? `${Math.round(benchmarkStats.summary.ai_win_rate)}%`
                : '—'}
              label="AI Win Rate"
              sublabel={benchmarkStats?.summary?.ai_win_rate != null
                ? 'vs human agents'
                : <span
                    onClick={() => onNavigate('benchmark')}
                    style={{ cursor: 'pointer', color: '#7c3aed', textDecoration: 'underline', textUnderlineOffset: 2 }}
                  >Run a Benchmark →</span>
              }
              color={benchmarkStats?.summary?.ai_win_rate != null ? '#f59e0b' : '#374151'}
            />
          )}
        </div>
      </div>

      {/* Tab overview cards */}
      <div>
        <h3 style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 15, margin: '0 0 14px' }}>
          What You Can Do
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {TAB_CARDS.map(card => {
            const isLocked = card.gated && !billing?.features?.benchmark;
            return (
              <div
                key={card.id}
                onClick={() => !isLocked && onNavigate(card.id)}
                style={{
                  background: '#1a1a2e',
                  border: `1px solid ${isLocked ? '#1f1f3a' : '#2d2d4e'}`,
                  borderRadius: 10, padding: '16px 20px',
                  cursor: isLocked ? 'default' : 'pointer',
                  opacity: isLocked ? 0.6 : 1,
                  transition: 'all 0.15s',
                  display: 'flex', gap: 16, alignItems: 'flex-start',
                }}
                onMouseEnter={e => { if (!isLocked) e.currentTarget.style.borderColor = '#7c3aed'; }}
                onMouseLeave={e => { if (!isLocked) e.currentTarget.style.borderColor = '#2d2d4e'; }}
              >
                {/* Icon */}
                <div style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
                }}>
                  {card.icon}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>
                      {card.name}
                    </span>
                    {isLocked && (
                      <span style={{
                        fontSize: 10, color: '#7c3aed', background: 'rgba(124,58,237,0.15)',
                        border: '1px solid rgba(124,58,237,0.3)', borderRadius: 4,
                        padding: '1px 6px', fontWeight: 600,
                      }}>
                        🔒 Starter+
                      </span>
                    )}
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{card.tagline}</span>
                  </div>
                  <p style={{ fontSize: 12, color: '#94a3b8', margin: 0, lineHeight: 1.6 }}>
                    {card.description}
                  </p>
                  {card.highlight && (
                    <div style={{
                      marginTop: 8, fontSize: 11, fontWeight: 600,
                      color: card.highlightColor || '#a78bfa',
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}>
                      <span>⚡</span> {card.highlight}
                    </div>
                  )}
                </div>

                {/* CTA */}
                {!isLocked && (
                  <div style={{
                    fontSize: 12, fontWeight: 600, color: '#7c3aed',
                    flexShrink: 0, paddingTop: 2, whiteSpace: 'nowrap',
                  }}>
                    {card.cta}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}
